import cx from "classnames";

import { Optional } from "common/utils/types";
import LinkButton, { LinkButtonProps } from "common/components/LinkButton";
import Angle from "common/components/Angle";

import styles from "../../styles/Banner.module.less";

export type BannerVariant =
  | "primary"
  | "promotion"
  | "promotion-dark"
  | "danger"
  | "success"
  | "warning"
  | "info";

type BannerProps = {
  children: React.ReactNode;
  inline?: boolean;
  variant?: BannerVariant;
  className?: string;
};

const Banner = ({ variant = "primary", inline, className, children }: BannerProps) => {
  return (
    <div
      className={cx(
        styles.container,
        styles[`variant-${variant}`],
        inline && styles.inline,
        className,
      )}
    >
      {children}
    </div>
  );
};

type BannerContentProps = {
  label: React.ReactNode;
  description?: Optional<React.ReactNode>;
};

const BannerContent = ({ label, description }: BannerContentProps) => {
  return (
    <div className={styles.content}>
      <div className={styles.label}>{label}</div>
      {description && <div className={styles.description}>{description}</div>}
    </div>
  );
};

type BannerTextProps = {
  label: string;
  align?: "center" | "left";
  action?: LinkButtonProps;
};

const BannerText = ({ label, align = "center", action }: BannerTextProps) => {
  return (
    <div className={cx(styles.text, styles[align])}>
      <span>{label}</span>
      {action && <LinkButton {...action} className={styles.action} />}
    </div>
  );
};

type BannerLinkButtonProps = BannerProps & LinkButtonProps;

const BannerLinkButton = ({
  variant = "primary",
  inline,
  className,
  children,
  ...other
}: BannerLinkButtonProps) => {
  return (
    <LinkButton
      className={cx(
        styles.container,
        className,
        styles[`variant-${variant}`],
        inline && styles.inline,
        styles.clickable,
      )}
      {...other}
    >
      {children}
      <Angle direction="right" className={styles.icon} />
    </LinkButton>
  );
};

export default Object.assign(Banner, {
  LinkButton: BannerLinkButton,
  Content: BannerContent,
  Text: BannerText,
});
